import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Manager, Reference, Popper } from 'react-popper';
import Moment from 'moment';
import { extendMoment } from 'moment-range';

// import classNames from 'lib/classNames';
import styles from 'styles/date_range.module.scss';
import { Icon, Calendar } from 'components';

const moment = extendMoment(Moment);
const defaultPeriods = [
	{ label: 'Today', range: moment.range([moment().startOf('day'), moment().endOf('day')]) },
	{
		label: 'Yesterday',
		range: moment.range([moment().subtract(1, 'days').startOf('day'), moment().subtract(1, 'days').endOf('day')]),
	},
	{ label: 'This Week', range: moment.range([moment().startOf('week'), moment().endOf('week')]) },
	{
		label: 'Last Week',
		range: moment.range([
			moment().subtract(1, 'weeks').startOf('week'),
			moment().subtract(1, 'weeks').endOf('week'),
		]),
	},
	{
		label: 'This Month',
		range: moment.range([moment().startOf('month'), moment().endOf('month')]),
	},
	{
		label: 'Last Month',
		range: moment.range([
			moment().subtract(1, 'months').startOf('month'),
			moment().subtract(1, 'months').endOf('month'),
		]),
	},
	{ label: 'All Time', range: null },
];

const DateRange = ({ onChange, value, children, button, maxDate, className, minDate, style = {} }) => {
	const contentRef = useRef(null);
	const tagRef = useRef(null);
	const [visible, setVisible] = useState(false);
	const handleOutSideClick = (event) => {
		if (!contentRef.current) return;
		else if (contentRef.current.contains(event.target)) return;
		setVisible(false);
	};

	useEffect(() => {
		if (visible) document.addEventListener('mousedown', handleOutSideClick, false);
		else document.removeEventListener('mousedown', handleOutSideClick, false);
		return () => {
			document.removeEventListener('mousedown', handleOutSideClick, false);
		};
	}, [visible]);

	const formateUnLabeledRange = () => {
		const range = [...new Set(value.map((date) => moment(date).format('MMM DD YYYY')))];

		return range.length === 2 ? `${range[0]} - ${range[1]}` : range[0];
	};
	const formatTagText = () => {
		const isLabeled = defaultPeriods.find(({ range }) =>
			value && range ? moment.range(value).isSame(range) : value === range
		);
		return isLabeled ? isLabeled.label : formateUnLabeledRange();
	};

	return (
		<div style={style} className={className} ref={contentRef}>
			<Manager>
				<Reference>
					{({ ref }) => (
						<div
							ref={ref}
							style={{ width: '100%', ...(button.style || {}) }}
							className={styles.trigger}
							onClick={(event) => {
								if (tagRef.current && tagRef.current.contains(event.target)) return;
								setVisible(!visible);
							}}
							data-active={!!value}
						>
							{button.icon && <Icon icon={button.icon} className="mr-2" />}
							{button.text}

							<Tag
								forwardRef={tagRef}
								text={formatTagText()}
								closable={!!value}
								onClick={() => {
									onChange(null);
									setVisible(false);
								}}
							/>
						</div>
					)}
				</Reference>
				<Popper
					placement="right-start"
					positionFixed={false}
					modifiers={{
						preventOverflow: { enabled: true },
						offset: { offset: '100%r,-100%r', enabled: true },
					}}
				>
					{({ ref, style, placement, arrowProps }) => {
						return visible ? (
							<div>
								<div
									ref={ref}
									style={{
										...style,
										zIndex: 100,
										marginTop: 10,
										borderRadius: 4,
										boxShadow: '0 1px 5px rgba(0, 0, 0, 0.46)',
									}}
									data-placement={placement}
								>
									<DateRangePicker value={value} onChange={(value) => onChange(value)} />
									<div ref={arrowProps.ref} style={arrowProps.style} />
								</div>
							</div>
						) : null;
					}}
				</Popper>
			</Manager>
		</div>
	);
};

const Tag = ({ text, onClick, forwardRef, closable }) => {
	return (
		<div className={styles.tag}>
			<div className={styles.text}>{text}</div>
			{closable && (
				<div className={styles.close} ref={forwardRef} onClick={() => onClick()}>
					<svg
						height="14"
						width="14"
						viewBox="0 0 20 20"
						aria-hidden="true"
						focusable="false"
						className="css-tj5bde-Svg"
					>
						<path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
					</svg>
				</div>
			)}
		</div>
	);
};

export default DateRange;

const DateRangePicker = ({ periods, value, onChange }) => {
	return (
		<div className={styles.DateRangePicker}>
			<div className={styles.menu}>
				{periods.map(({ label, range }, i) => {
					const active = value && range ? moment.range(value).isSame(range) : value === range;
					return (
						<span key={i} data-active={active} onClick={() => onChange(range ? range.toDate() : null)}>
							{label}
						</span>
					);
				})}
			</div>
			<Calendar range={value} onChange={(value) => onChange(value)} />
		</div>
	);
};

DateRangePicker.propTypes = {};

DateRangePicker.defaultProps = {
	periods: defaultPeriods,
};
